import type { ReactNode } from 'react';

import { cx } from 'cva';
import { composeRenderProps } from 'react-aria-components/composeRenderProps';
import {
  Tag as AriaTag,
  TagGroup as AriaTagGroup,
  type TagGroupProps as AriaTagGroupProps,
  type TagProps as AriaTagProps,
  TagList,
  type TagListProps,
} from 'react-aria-components/TagGroup';

import { Button } from '../button/button';
import { chipVariants } from '../chip/chip';
import { FieldDescription } from '../form/field_description';
import { FieldLabel } from '../form/field_label';
import { Icon } from '../icon/icon';

import styles from './tag_group.module.css';

export type TagGroupProps<T> = Omit<AriaTagGroupProps, 'children'> &
  Pick<TagListProps<T>, 'items' | 'children' | 'renderEmptyState'> & {
    isRequired?: boolean;
    label?: string;
    description?: string;
    trailingAction?: ReactNode;
  };

/**
 * TagGroup displays a collection of tags with keyboard navigation, optional
 * selection, and tag removal support via React Aria.
 *
 * See https://react-spectrum.adobe.com/react-aria/TagGroup.html for the
 * underlying implementation.
 */
export function TagGroup<T extends object>({
  isRequired,
  label,
  description,
  trailingAction,
  items,
  children,
  renderEmptyState,
  className,
  ...props
}: TagGroupProps<T>) {
  const classes = cx(styles.tag_group, className);
  return (
    <AriaTagGroup {...props} className={classes}>
      {label && <FieldLabel isRequired={isRequired}>{label}</FieldLabel>}
      <div className={styles.tag_list_wrapper}>
        <TagList
          className={styles.tag_list}
          items={items}
          renderEmptyState={renderEmptyState}
        >
          {children}
        </TagList>
        {trailingAction}
      </div>
      {description && <FieldDescription>{description}</FieldDescription>}
    </AriaTagGroup>
  );
}

export type TagProps = AriaTagProps;

/**
 * Tag renders an individual pill-style tag for use inside `TagGroup`.
 *
 * See https://react-spectrum.adobe.com/react-aria/TagGroup.html for the
 * underlying implementation.
 */
export function Tag({ children, ...props }: TagProps) {
  const textValue = typeof children === 'string' ? children : undefined;
  const classes = chipVariants({
    variant: 'pill',
    className: styles.tag,
  });

  return (
    <AriaTag textValue={textValue} {...props} className={classes}>
      {composeRenderProps(children, (composedChildren, { allowsRemoving }) => (
        <>
          <span className={styles.tag_label} title={textValue}>
            {composedChildren}
          </span>
          {allowsRemoving && (
            <Button
              slot="remove"
              variant="tertiary"
              size="s"
              round
              excludeFromTabOrder
              icon={
                <Icon
                  className={styles.tag_remove_icon}
                  name="deletion_erase"
                />
              }
            />
          )}
        </>
      ))}
    </AriaTag>
  );
}
